import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { userService } from '../services/api';
import PessoaList from '../components/Pessoa/PessoaList';
import PessoaForm from '../components/Pessoa/PessoaForm';
import { useAuth } from '../contexts/auth';

const Pessoas = () => {
  const { user, validateToken, setUser } = useAuth();
  const [pessoas, setPessoas] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [openForm, setOpenForm] = useState(false);
  const [selectedPessoa, setSelectedPessoa] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [permissionError, setPermissionError] = useState(null);

  // Verificar se o usuário tem lares vinculados
  const hasLinkedHomes = user?.profile === 'Administrador' || (user?.laresVinculados && user.laresVinculados.length > 0);
  
  const fetchPessoas = useCallback(async () => {
    try {
      const response = await userService.getAll();
      console.log('Pessoas carregadas:', response.data);
      setPessoas(response.data);
      setPermissionError(null); // Limpar erros de permissão se a requisição for bem-sucedida
    } catch (error) {
      console.error('Erro ao carregar pessoas:', error);
      
      // Verificar se é um erro de permissão (403)
      if (error.status === 403) {
        setPermissionError({
          message: error.message,
          details: error.details || 'Você não tem permissão para acessar esta funcionalidade.'
        });
        setPessoas([]); // Limpar a lista de pessoas
      } else {
        showSnackbar('Erro ao carregar pessoas: ' + (error.message || 'Erro desconhecido'), 'error');
      }
    }
  }, []);

  useEffect(() => {
    fetchPessoas();
  }, [fetchPessoas]);

  // Atualizar informações do usuário ao carregar a página
  useEffect(() => {
    const updateUserInfo = async () => {
      try {
        // Validar o token e atualizar as informações do usuário
        await validateToken();
        console.log('Informações do usuário atualizadas');
      } catch (error) {
        console.error('Erro ao atualizar informações do usuário:', error);
      }
    };
    
    updateUserInfo();
  }, [validateToken]);

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const filteredPessoas = pessoas.filter(pessoa =>
    pessoa.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    pessoa.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    pessoa.profile.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleOpenForm = async (pessoa = null) => {
    try {
      if (pessoa) {
        console.log('Buscando detalhes da pessoa:', pessoa.id, 'Modo:', pessoa.mode);
        
        // Se o modo for 'view', apenas use os dados que já temos
        if (pessoa.mode === 'view') {
          console.log('Modo de visualização: usando dados existentes');
          setSelectedPessoa({ ...pessoa });
          setOpenForm(true);
          return;
        }
        
        // Se o modo for 'edit', busque dados atualizados do servidor
        const response = await userService.getById(pessoa.id);
        console.log('Detalhes da pessoa carregados:', response.data);
        setSelectedPessoa({ ...response.data, mode: pessoa.mode });
      } else {
        setSelectedPessoa(null);
      }
      setOpenForm(true);
    } catch (error) {
      console.error('Erro ao carregar detalhes da pessoa:', error);
      
      // Verificar se é um erro de permissão (403)
      if (error.response && error.response.status === 403) {
        showSnackbar(`Erro de permissão: ${error.response.data.message || 'Você não tem permissão para esta ação'}`, 'error');
      } else {
        showSnackbar('Erro ao carregar detalhes da pessoa: ' + (error.message || 'Erro desconhecido'), 'error');
      }
    }
  };

  const handleCloseForm = () => {
    setSelectedPessoa(null);
    setOpenForm(false);
  };

  const handleSubmit = async (formData) => {
    try {
      // Verificar se formData é um objeto FormData válido
      if (!(formData instanceof FormData)) {
        throw new Error('Dados inválidos recebidos do formulário');
      }

      console.log('Dados do formulário a serem enviados:');
      for (let pair of formData.entries()) {
        console.log(pair[0] + ': ' + pair[1]);
      }

      let response;
      if (selectedPessoa) {
        console.log(`Enviando PUT request para /api/users/${selectedPessoa.id}`);
        response = await userService.update(selectedPessoa.id, formData);
        console.log('Resposta do servidor após PUT:', response.data);
      } else {
        console.log('Enviando POST request para /api/users');
        response = await userService.create(formData);
        console.log('Resposta do servidor após POST:', response.data);
      }
      
      await fetchPessoas(); // Recarregar a lista após a atualização
      handleCloseForm(); // Fechar o formulário após salvamento bem-sucedido
      showSnackbar(selectedPessoa ? 'Pessoa atualizada com sucesso!' : 'Pessoa criada com sucesso!', 'success');
    } catch (error) {
      console.error('Erro ao salvar pessoa:', error);
      
      // Verificar se é um erro de permissão (403)
      if (error.status === 403) {
        showSnackbar(`Erro de permissão: ${error.message}\n${error.details || ''}`, 'error');
      } else {
        showSnackbar(error.message || 'Erro ao salvar pessoa', 'error');
      }
    }
  };

  const handleDelete = (pessoa) => {
    setConfirmDelete(pessoa);
  };

  const confirmDeletePessoa = async () => {
    try {
      await userService.delete(confirmDelete.id);
      showSnackbar('Pessoa excluída com sucesso');
      fetchPessoas();
    } catch (error) {
      // Verificar se é um erro de permissão (403)
      if (error.status === 403) {
        showSnackbar(`Erro de permissão: ${error.message}`, 'error');
      } else {
        showSnackbar(error.message || 'Erro ao excluir pessoa', 'error');
      }
    } finally {
      setConfirmDelete(null);
    }
  };

  const handleToggleActive = async (pessoa) => {
    try {
      await userService.toggleActive(pessoa.id);
      showSnackbar(`Pessoa ${pessoa.active ? 'desativada' : 'ativada'} com sucesso`);
      fetchPessoas();
    } catch (error) {
      // Verificar se é um erro de permissão (403)
      if (error.status === 403) {
        showSnackbar(`Erro de permissão: ${error.message}`, 'error');
      } else {
        showSnackbar(error.message || 'Erro ao alterar status', 'error');
      }
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Mensagem informativa com base no perfil do usuário
  const getNoHomesMessage = () => {
    if (user?.profile === 'Gestor') {
      return 'Você não possui lares vinculados. Como gestor, você precisa estar vinculado a pelo menos um lar para gerenciar usuários. Entre em contato com um administrador para vincular você a um ou mais lares.';
    } else if (user?.profile !== 'Administrador') {
      return 'Você não possui lares vinculados. Entre em contato com um administrador para vincular você a um ou mais lares.';
    }
    return '';
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" component="h2">
            Gerenciar Pessoas
          </Typography>
          {(user?.profile === 'Administrador' || user?.profile === 'Gestor') && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenForm()}
              disabled={!hasLinkedHomes && user?.profile !== 'Administrador'}
            >
              Nova Pessoa
            </Button>
          )}
        </Box>

        {/* Mensagem informativa para usuários sem lares vinculados */}
        {!hasLinkedHomes && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InfoIcon />
              <Typography variant="body2">{getNoHomesMessage()}</Typography>
            </Box>
          </Alert>
        )}

        {/* Exibir erro de permissão se houver */}
        {permissionError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="subtitle1">{permissionError.message}</Typography>
            {permissionError.details && (
              <Typography variant="body2">{permissionError.details}</Typography>
            )}
          </Alert>
        )}

        <TextField
          fullWidth
          variant="outlined"
          placeholder="Pesquisar por nome, email ou perfil..."
          value={searchTerm}
          onChange={handleSearch}
          sx={{ mb: 3 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton onClick={clearSearch} edge="end">
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            )
          }}
        />

        {pessoas.length === 0 && hasLinkedHomes && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Nenhuma pessoa encontrada.
          </Alert>
        )}

        <PessoaList
          pessoas={filteredPessoas}
          onEdit={handleOpenForm}
          onDelete={handleDelete}
          onToggleActive={handleToggleActive}
        />
      </Paper>

      <PessoaForm
        open={openForm}
        onClose={handleCloseForm}
        onSubmit={handleSubmit}
        pessoa={selectedPessoa}
        mode={selectedPessoa?.mode || (selectedPessoa ? 'edit' : 'create')}
        setUser={setUser}
      />

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          Tem certeza que deseja excluir {confirmDelete?.name}?
          Esta ação não pode ser desfeita.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancelar</Button>
          <Button onClick={confirmDeletePessoa} color="error" variant="contained">
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Pessoas;
